import React from "react";
import { ClipboardList, ExternalLink, FileCheck2, ListChecks, Route } from "lucide-react";
import { EXAM_PROVIDER_SOURCE, getSyllabus, PERMIT_FACTS, PERMIT_REQUIREMENTS, SOURCE } from "../../data/examSyllabus";
import { CONTACT } from "../../data/votraLocations";

const INK = "hsl(222.2, 47.4%, 11.2%)";
const BRAND = "hsl(210,100%,45%)";

const kr = (n) => n.toLocaleString("da-DK") + " kr.";

const Step = ({ n, title, children, testid }) => (
  <li className="relative pl-11 pb-6 last:pb-0" data-testid={testid}>
    <span
      className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white"
      style={{ backgroundColor: BRAND }}
    >
      {n}
    </span>
    <span className="absolute left-4 top-9 bottom-0 w-px bg-slate-200 last:hidden" aria-hidden />
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    <p className="mt-1 text-slate-600 leading-relaxed text-[15px]">{children}</p>
  </li>
);

/**
 * Pilotstandarden for de myndighedsrelaterede vognmandsforløb: hele vejen fra
 * tilmelding til tilladelse — i rigtig HTML, så både kursister og AI-systemer
 * kan læse præcis hvad prøven dækker, og hvad der kræves bagefter.
 */
export const ExamSyllabusSection = ({ group }) => {
  const syllabus = getSyllabus(group.syllabus);
  if (!syllabus) return null;

  const venues = group.examVenues || [];
  const attempts = group.examAttempts;
  const isVognmand = group.category === "vognmand";
  const source = syllabus.source || SOURCE;

  return (
    <>
      {/* ── Sådan foregår det (kun selvstudieforløbene med egen prøve) ───── */}
      {group.selfStudy && (
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-process">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: INK }}>
          <Route className="h-5 w-5" /> Sådan foregår det hos Votra
        </h2>
        <ol className="mt-2">
          <Step n="1" title="Du tilmelder dig en prøvedato" testid="process-step-1">
            Du vælger en af de kommende prøvedatoer på denne side. Er du i tvivl om, hvornår du er klar,
            ringer du til os på{" "}
            <a href={`tel:${CONTACT.phone}`} className="font-medium hover:underline" style={{ color: BRAND }}>
              {CONTACT.phoneDisplay}
            </a>{" "}
, så finder vi datoen sammen.
          </Step>
          <Step n="2" title="Du får adgang til online kursusmaterialet" testid="process-step-2">
            Materialet dækker samtlige {syllabus.areas.length} emneområder, som Færdselsstyrelsen kræver
            ({syllabus.name}). Du læser hjemmefra i dit eget tempo, der er ingen fremmødeundervisning og
            ingen mødepligt.
          </Step>
          <Step n="3" title="Vi tilmelder dig prøven" testid="process-step-3">
            Votra står for tilmeldingen til prøven, så du ikke selv skal finde rundt i papirarbejdet.
            {attempts ? ` Der er ${attempts} prøveforsøg inkluderet i prisen.` : ""}
          </Step>
          <Step n="4" title="Du går til prøven hos os" testid="process-step-4">
            Prøven er skriftlig, udarbejdes og tilrettelægges af Færdselsstyrelsen og aflægges i styrelsens
            elektroniske prøveaflæggelsessystem.
            {venues.length ? ` Du aflægger den hos Votra i ${venues.join(" eller ")}.` : ""}{" "}
            Votra Danmark ApS er optaget på Færdselsstyrelsens liste over godkendte prøveudbydere.
          </Step>
        </ol>
        <p className="mt-2 text-sm text-slate-500">
          <a
            href={EXAM_PROVIDER_SOURCE.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:underline"
            style={{ color: BRAND }}
            data-testid="exam-provider-source"
          >
            Se Votra på Færdselsstyrelsens liste over godkendte prøveudbydere
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </p>
      </div>
      )}

      {/* ── Prøvens emneområder (myndighedsfastsat) ─────────────────────── */}
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-syllabus">
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2" style={{ color: INK }}>
          <ListChecks className="h-5 w-5" /> Det bliver du prøvet i
        </h2>
        <p className="text-slate-600 leading-relaxed">
          Færdselsstyrelsen fastsætter, hvilke emner kurset skal dække, og prøven stiller spørgsmål inden
          for dem. Nedenfor står alle {syllabus.areas.length} emneområder fra {syllabus.name}, det er
          pensum, ikke en markedsføringsliste. {syllabus.lessonNote}
        </p>

        <div className="mt-5 space-y-5">
          {syllabus.areas.map((area) => (
            <section key={area.letter} data-testid={`syllabus-area-${area.letter}`}>
              <h3 className="flex items-baseline gap-2 text-base font-semibold text-slate-900">
                <span
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold"
                  style={{ backgroundColor: "hsl(210,100%,45%,0.1)", color: BRAND }}
                >
                  {area.letter}
                </span>
                {area.title}
              </h3>
              <ul className="mt-1.5 ml-8 list-disc space-y-1 text-[15px] text-slate-600">
                {area.points.map((p, i) => (
                  <li key={i} className="leading-relaxed">{p}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="mt-6 text-sm text-slate-500">
          Kilde:{" "}
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:underline"
            style={{ color: BRAND }}
          >
            {source.label} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </p>
      </div>

      {/* ── Efter bestået prøve: de øvrige krav til tilladelsen ─────────── */}
      {isVognmand && (
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-permit-requirements">
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2" style={{ color: INK }}>
          <FileCheck2 className="h-5 w-5" /> Når prøven er bestået, hvad kræver tilladelsen så?
        </h2>
        <p className="text-slate-600 leading-relaxed">
          Beviset for bestået prøve dækker kravet om <strong>faglige kvalifikationer</strong>. Før
          Færdselsstyrelsen udsteder tilladelsen, skal virksomheden også opfylde kravene nedenfor, og de
          skal være opfyldt i hele tilladelsens gyldighedsperiode.
        </p>

        <dl className="mt-4 space-y-3">
          {PERMIT_REQUIREMENTS.map((r) => (
            <div key={r.title} className="rounded-lg border border-slate-200 p-4">
              <dt className="flex items-center gap-2 font-semibold text-slate-900">
                <ClipboardList className="h-4 w-4" style={{ color: BRAND }} />
                {r.title}
              </dt>
              <dd className="mt-1 text-[15px] text-slate-600 leading-relaxed">{r.text}</dd>
            </div>
          ))}
        </dl>

        <ul className="mt-4 space-y-1.5 text-[15px] text-slate-600">
          <li>
            <span className="text-slate-500">Gebyr:</span>{" "}
            <strong>{kr(PERMIT_FACTS.feePerPermit)}</strong> for tilladelsen og{" "}
            <strong>{kr(PERMIT_FACTS.feePerCopy)}</strong> pr. bekræftet kopi (betales til Færdselsstyrelsen,
            ikke til Votra).
          </li>
          <li>
            <span className="text-slate-500">Gyldighed:</span> op til {PERMIT_FACTS.validityYears} år,
            hvorefter tilladelsen skal fornyes.
          </li>
          <li>
            <span className="text-slate-500">Sagsbehandling:</span> Færdselsstyrelsens mål er en afgørelse
            inden {PERMIT_FACTS.decisionTargetDays} dage.
          </li>
        </ul>

        <p className="mt-4 text-sm text-slate-500">
          Kilde:{" "}
          <a
            href={PERMIT_FACTS.source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:underline"
            style={{ color: BRAND }}
          >
            {PERMIT_FACTS.source.label} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </p>
      </div>
      )}
    </>
  );
};

export default ExamSyllabusSection;
